import React, { useContext, useEffect, useMemo, useRef, useState } from "react"
import { View, ScrollView, StyleSheet, Text, TouchableOpacity } from "react-native"
import { connect } from "react-redux"
import * as amplitude from "@amplitude/analytics-react-native"
import FilledButtonM from "@common/components/button/FilledButtonM"
import { theme } from "@common/theme/theme"
import { DimensionsContext } from "@common/utils/mobileUtils"
import { useRoute } from "@react-navigation/native"
import { apiRequest } from "@data/redux/actions/api"
import BrevetCountdownBanner from "./components/BrevetCountdownBanner"
import BrevetFunnelHeader from "./components/BrevetFunnelHeader"
import BrevetChip from "./components/BrevetChip"
import BrevetModulesList from "./components/BrevetModulesList"
import { useBrevetFunnel } from "./context/BrevetFunnelContext"
import {
    BREVET_SUBJECTS,
    getModulesForSubjects,
} from "./constants/brevetModules"
import { brevetFunnelService } from "./services/brevetFunnelService"

const SUBJECT_ICONS = {
    maths: "📐",
    francais: "📖",
    histoire_geo: "🗺️",
    sciences: "🔬",
    oral: "🎤",
}

const OBJECTIVES = [
    { id: "diplome", label: "Décrocher le brevet et en finir sereinement" },
    { id: "mention", label: "Partir en vacances avec la mention en poche" },
    { id: "mention_tb", label: "Clôturer le collège avec brio (mention TB)" },
]

const MAX_SUBJECTS = 3

const BrevetDiagnosticStep1Screen = (props) => {
    const { navigation, reduxFunnelStateId, reduxInitUser } = props
    const dimensions = useContext(DimensionsContext)
    const route = useRoute()
    const { state, update, reset } = useBrevetFunnel()
    const scrollRef = useRef(null)
    const subjectsAnchorY = useRef(0)
    const objectiveAnchorY = useRef(0)
    const amplitudeTrackedRef = useRef(false)

    // Hydratation depuis query params (1x au mount)
    useEffect(() => {
        const params = route.params || {}
        // Normalise the class param coming from the LP — accents are commonly stripped in URLs.
        // Backend `User::$studentClasses` enforces accented values (3ème, 4ème, …).
        const normaliseClass = (raw) => {
            if (!raw) return raw
            const map = {
                "3eme": "3ème",
                "4eme": "4ème",
                "5eme": "5ème",
                "6eme": "6ème",
                "1ere": "1ère",
                "tle": "Tle",
            }
            return map[raw.toLowerCase()] || raw
        }

        // HubSpot-gated entry: LP redirects to /brevet/start?submissionGUID=xxx.
        // The submission contains firstname/email/student_class (subscription_1_to_5 form).
        // /init creates the User + FastTrackState and returns the user payload so we
        // can hydrate the Context. Front never sees PII in the URL.
        const submissionGUID =
            params.submissionGUID || params.submission_guid || null

        // Defensive guard: if the URL carries a new submissionGUID different from the
        // one persisted in sessionStorage, the prior session was for another parent.
        // Reset the Context so the fresh HubSpot payload hydrates cleanly (otherwise
        // stale firstname/email/funnelStateId from the previous parent stay sticky).
        const guidMismatch =
            submissionGUID &&
            state.submissionGUID &&
            state.submissionGUID !== submissionGUID
        if (guidMismatch) {
            reset()
        }

        // Always call /brevet-funnel/init when we have a submissionGUID in the URL.
        // The backend endpoint is idempotent: find-or-create User + ensure the
        // FastTrackState is in a brevet_* state (promotes 'init' or any non-brevet
        // state coming from FastTrackController::init to 'brevet_started').
        // Skipping based on a cached funnelStateId leaves the state in a non-brevet
        // flow when the parent comes via /register/fast-track (where FastTrack::init
        // seeded the state at 'init' before lead_intent routed them here).
        if (submissionGUID) {
            update({ submissionGUID })
            props.apiRequest(
                brevetFunnelService.initFromHubSpot,
                {},
                { submission_guid: submissionGUID }
            )
            // Stash referral code if present alongside submissionGUID (LP can append it).
            if (params.ref && !state.referralCode) {
                update({ referralCode: params.ref })
            }
        } else {
            // Direct path — historical query params (used for tests / direct links).
            const studentClass =
                normaliseClass(params.class) || state.studentClass
            const patch = {}
            if (params.firstname && !state.studentFirstname)
                patch.studentFirstname = params.firstname
            if (studentClass && !state.studentClass)
                patch.studentClass = studentClass
            if (params.email && !state.parentEmail)
                patch.parentEmail = params.email
            if (params.ref && !state.referralCode)
                patch.referralCode = params.ref
            if (Object.keys(patch).length) update(patch)

            // Lead capture — POST start dès qu'on a un email. Le funnel_state_id revient
            // par le reducer Redux ; on le re-synchronise dans le context dès qu'il est dispo.
            const effectiveEmail = params.email || state.parentEmail
            if (!state.funnelStateId && !reduxFunnelStateId && effectiveEmail) {
                props.apiRequest(
                    brevetFunnelService.startFunnel,
                    {},
                    {
                        firstname:
                            params.firstname || state.studentFirstname || "",
                        student_class: studentClass || "3ème",
                        parent_email: effectiveEmail,
                        referral_code:
                            params.ref || state.referralCode || null,
                    }
                )
            }
        }

        // Peek referrer firstname — endpoint hors scope V1, conserve un noop côté UI
        // (cf. stub brevetFunnelService.peekReferralCode).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Quand Redux reçoit funnel_state_id (success de startFunnel ou initFromHubSpot),
    // on l'aligne dans le context.
    useEffect(() => {
        if (reduxFunnelStateId && reduxFunnelStateId !== state.funnelStateId) {
            update({ funnelStateId: reduxFunnelStateId })
        }
    }, [reduxFunnelStateId, state.funnelStateId, update])

    // Quand /init renvoie le user (firstname/email/student_class depuis HubSpot),
    // on pousse les champs dans le Context côté UI. On garde la priorité aux valeurs
    // que l'utilisateur aurait pu déjà saisir manuellement (state.* gagne).
    useEffect(() => {
        if (!reduxInitUser) return
        const patch = {}
        if (reduxInitUser.firstname && !state.studentFirstname)
            patch.studentFirstname = reduxInitUser.firstname
        if (reduxInitUser.email && !state.parentEmail)
            patch.parentEmail = reduxInitUser.email
        if (reduxInitUser.student_class && !state.studentClass)
            patch.studentClass = reduxInitUser.student_class
        if (Object.keys(patch).length) update(patch)
    }, [
        reduxInitUser,
        state.studentFirstname,
        state.parentEmail,
        state.studentClass,
        update,
    ])

    // Amplitude — funnel started (1x, dès qu'on a un funnel_state_id côté state).
    useEffect(() => {
        if (!amplitudeTrackedRef.current && state.funnelStateId) {
            amplitudeTrackedRef.current = true
            amplitude.track("brevet_funnel_started", {
                funnel_state_id: state.funnelStateId,
                referral_code: state.referralCode || null,
            })
        }
    }, [state.funnelStateId, state.referralCode])

    const [subjects, setSubjects] = useState(state.subjects || [])
    const [selectedModules, setSelectedModules] = useState(
        state.selectedModules || []
    )
    const [objective, setObjective] = useState(state.objective || null)
    const [error, setError] = useState(null)

    const toggleSubject = (id) => {
        setError(null)
        if (subjects.includes(id)) {
            const nextSubjects = subjects.filter((s) => s !== id)
            const removedModuleIds = getModulesForSubjects([id]).map((m) => m.id)
            setSubjects(nextSubjects)
            setSelectedModules(
                selectedModules.filter((m) => !removedModuleIds.includes(m))
            )
        } else if (subjects.length < MAX_SUBJECTS) {
            // Chapitres désélectionnés par défaut : l'utilisateur coche ce qui compte.
            // Le fallback "tous chapitres" est appliqué en Step 3 quand selectedModules est vide.
            setSubjects([...subjects, id])
        }
    }

    const toggleModule = (moduleId) => {
        setSelectedModules(
            selectedModules.includes(moduleId)
                ? selectedModules.filter((m) => m !== moduleId)
                : [...selectedModules, moduleId]
        )
    }

    const onContinue = () => {
        if (!subjects.length) {
            setError("missing_subjects")
            scrollRef.current?.scrollTo({ y: subjectsAnchorY.current, animated: true })
            return
        }
        if (!objective) {
            setError("missing_objective")
            scrollRef.current?.scrollTo({ y: objectiveAnchorY.current, animated: true })
            return
        }
        update({ subjects, selectedModules, objective })
        navigation.navigate("brevet/diag-2")
    }

    const firstname = state.studentFirstname || route.params?.firstname || "votre enfant"
    const containerWidth = Math.min(dimensions?.width || 400, 460)
    const referrerName = state.referrerFirstname

    const allModulesIds = useMemo(
        () => getModulesForSubjects(subjects).map((m) => m.id),
        [subjects]
    )
    const allModulesChecked =
        allModulesIds.length > 0 &&
        allModulesIds.every((id) => selectedModules.includes(id))

    const toggleAllModules = () => {
        if (allModulesChecked) setSelectedModules([])
        else setSelectedModules(allModulesIds)
    }

    return (
        <View style={styles.root}>
            <View style={[styles.frame, { width: containerWidth }]}>
                <BrevetFunnelHeader
                    step={1}
                    total={4}
                    hideBack
                    onBack={() => navigation.goBack()}
                />
                <ScrollView
                    ref={scrollRef}
                    contentContainerStyle={styles.content}
                    showsVerticalScrollIndicator={false}
                >
                    <BrevetCountdownBanner />

                    {referrerName ? (
                        <View style={styles.refBanner}>
                            <Text style={styles.refBannerText}>
                                🎁 <Text style={styles.refBannerBold}>-50 € grâce à {referrerName}</Text> — réduction appliquée automatiquement au paiement.
                            </Text>
                        </View>
                    ) : null}

                    <Text
                        style={styles.title}
                        onLayout={(e) =>
                            (subjectsAnchorY.current = e.nativeEvent.layout.y)
                        }
                    >
                        Choisir les matières à travailler
                    </Text>

                    <View style={styles.chipsRow}>
                        {BREVET_SUBJECTS.map((s) => (
                            <BrevetChip
                                key={s.id}
                                label={`${SUBJECT_ICONS[s.id] || ""}  ${s.label}`}
                                selected={subjects.includes(s.id)}
                                disabled={
                                    !subjects.includes(s.id) &&
                                    subjects.length >= MAX_SUBJECTS
                                }
                                onPress={() => toggleSubject(s.id)}
                            />
                        ))}
                    </View>
                    {error === "missing_subjects" && (
                        <Text style={styles.errorText}>
                            Sélectionnez au moins une matière.
                        </Text>
                    )}

                    {subjects.length > 0 && (
                        <>
                            <View style={styles.modulesHeaderRow}>
                                <Text style={[styles.title, { flex: 1, marginBottom: 0 }]}>
                                    Cibler les chapitres prioritaires
                                </Text>
                                <TouchableOpacity
                                    onPress={toggleAllModules}
                                    activeOpacity={0.7}
                                    style={styles.modulesToggle}
                                >
                                    <Text style={styles.modulesToggleLabel}>
                                        {allModulesChecked ? "Tout décocher" : "Tout cocher"}
                                    </Text>
                                </TouchableOpacity>
                            </View>
                            <BrevetModulesList
                                subjects={subjects}
                                selectedModules={selectedModules}
                                onToggleModule={toggleModule}
                                style={{ marginTop: 10 }}
                            />
                        </>
                    )}

                    <Text
                        style={[styles.title, { marginTop: 26 }]}
                        onLayout={(e) =>
                            (objectiveAnchorY.current = e.nativeEvent.layout.y)
                        }
                    >
                        Choisir l'objectif au Brevet
                    </Text>
                    <View style={[styles.chipsColumn, { marginTop: 10 }]}>
                        {OBJECTIVES.map((o) => (
                            <BrevetChip
                                key={o.id}
                                label={o.label}
                                selected={objective === o.id}
                                onPress={() => {
                                    setError(null)
                                    setObjective(o.id)
                                }}
                                style={styles.objectiveChip}
                            />
                        ))}
                    </View>
                    {error === "missing_objective" && (
                        <Text style={styles.errorText}>
                            Choisissez un objectif pour personnaliser la reco.
                        </Text>
                    )}
                </ScrollView>
                <View style={styles.footer}>
                    <FilledButtonM
                        label="Continuer"
                        onPress={onContinue}
                        style={{ width: "100%", marginBottom: 0 }}
                    />
                </View>
            </View>
        </View>
    )
}

const styles = StyleSheet.create({
    root: {
        flex: 1,
        alignItems: "center",
        backgroundColor: theme.colors.whiteAlmost,
    },
    frame: {
        flex: 1,
        backgroundColor: theme.colors.white,
        paddingHorizontal: 20,
    },
    content: {
        paddingTop: 14,
        paddingBottom: 24,
    },
    refBanner: {
        backgroundColor: theme.colors.successLight,
        borderRadius: 10,
        paddingVertical: 10,
        paddingHorizontal: 12,
        marginBottom: 14,
    },
    refBannerText: {
        fontFamily: "Montserrat-Regular",
        fontSize: 12,
        color: theme.colors.successDark,
    },
    refBannerBold: {
        fontFamily: "Montserrat-Bold",
    },
    title: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 22,
        color: theme.colors.primary,
        marginBottom: 8,
    },
    subtitle: {
        fontFamily: "Montserrat-Regular",
        fontSize: 13,
        color: theme.colors.grey,
        marginBottom: 16,
    },
    sectionTitle: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 17,
        color: theme.colors.primary,
        marginBottom: 6,
    },
    sectionSubtitle: {
        fontFamily: "Montserrat-Regular",
        fontSize: 12,
        color: theme.colors.grey,
    },
    chipsRow: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 8,
    },
    chipsColumn: {
        flexDirection: "column",
        gap: 8,
    },
    modulesHeaderRow: {
        flexDirection: "row",
        alignItems: "flex-start",
        gap: 10,
        marginTop: 22,
    },
    modulesToggle: {
        paddingHorizontal: 10,
        paddingVertical: 5,
        borderRadius: 14,
        borderWidth: 1.5,
        borderColor: theme.colors.primary,
        backgroundColor: theme.colors.white,
    },
    modulesToggleLabel: {
        fontFamily: "Montserrat-Bold",
        fontSize: 10,
        color: theme.colors.primary,
    },
    objectiveChip: {
        alignSelf: "flex-start",
    },
    errorText: {
        fontFamily: "Montserrat-Bold",
        fontSize: 12,
        color: theme.colors.error,
        marginTop: 8,
    },
    footer: {
        paddingVertical: 14,
        borderTopWidth: 1,
        borderTopColor: theme.colors.lightMore,
        backgroundColor: theme.colors.white,
    },
})

const mapStateToProps = (state) => ({
    reduxFunnelStateId: state.data?.brevetFunnel?.funnelStateId ?? null,
    reduxInitUser: state.data?.brevetFunnel?.initUser ?? null,
})

const mapDispatchToProps = {
    apiRequest,
}

export default connect(mapStateToProps, mapDispatchToProps)(
    BrevetDiagnosticStep1Screen
)
